/**
 * 基础信息表单组件
 * 用于收集用户的基本信息
 * 
 * @component BasicInfoForm
 * @emits submit - 表单提交事件
 * @emits update - 表单更新事件
 */

<template>
  <div class="collapsible-section" id="基本信息">
    <div class="collapsible-header" @click="toggleBasicInfo">
      <div class="header-left">
        <el-icon :size="24" :class="{ 'header-arrow': !showBasicInfo }">
          <svg-icon name="arrow-active" />
        </el-icon>
        <span class="header-title">基本信息</span>
        <el-tag size="small" type="info" class="field-count">4 个字段</el-tag>
      </div>
    </div>
    
    <div class="collapsible-content" :class="{ 'expanded': showBasicInfo }">
      <div class="form-row">
        <div class="form-item">
          <label>发明名称</label>
          <el-tooltip placement="top" :content="formData.title">
            <input 
              v-if="showTooltip"
              type="text" 
              v-model="formData.title" 
              disabled
            />
          </el-tooltip>
          <input 
            v-if="!showTooltip"
            type="text" 
            v-model="formData.title" 
            :disabled="disabled"
            placeholder="请输入发明名称，例如：一种基于机器学习的图像识别方法"
            @input="handleInput('title', $event.target.value)"
          />
        </div>
        <div class="form-item">
          <label>发明人</label>
          <el-tooltip placement="top" :content="formData.inventor">
            <input 
              v-if="showTooltip"
              type="text" 
              v-model="formData.inventor" 
              disabled
            />
          </el-tooltip>
          <input 
            v-if="!showTooltip"
            type="text" 
            v-model="formData.inventor" 
            :disabled="disabled"
            placeholder="请输入发明人姓名，多个发明人用逗号分隔"
            @input="handleInput('inventor', $event.target.value)"
          />
        </div>
      </div>
      <div class="form-row">
        <div class="form-item">
          <label>技术领域</label>
          <el-tooltip placement="top" :content="formData.field">
            <input 
              v-if="showTooltip"
              type="text" 
              v-model="formData.field" 
              disabled
            />
          </el-tooltip>
          <input 
            v-if="!showTooltip"
            type="text" 
            :disabled="disabled"
            v-model="formData.field" 
            placeholder="请输入技术领域，例如：人工智能、计算机视觉"
            @input="handleInput('field', $event.target.value)"
          />
        </div>
        <div class="form-item">
          <label>项目归属</label>
          <el-tooltip placement="top" :content="formData.project">
            <input 
              v-if="showTooltip"
              type="text" 
              v-model="formData.project" 
              disabled
            />
          </el-tooltip>
          <input 
            v-if="!showTooltip"
            type="text" 
            :disabled="disabled"
            v-model="formData.project" 
            placeholder="请输入项目归属，例如：XX研究院AI项目组"
            @input="handleInput('project', $event.target.value)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch } from 'vue'
import { InfoFilled, ArrowDown } from '@element-plus/icons-vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  disabled: {
    type: Boolean,
    required: false
  },
  showTooltip: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const showBasicInfo = ref(true)
const formData = ref({ ...props.modelValue })

const toggleBasicInfo = () => {
  showBasicInfo.value = !showBasicInfo.value
}

const handleInput = (field, value) => {
  formData.value[field] = value
  console.log(formData.value)
  emit('update:modelValue', { ...formData.value })
}

watch(() => props.modelValue, (newVal) => {
  formData.value = { ...newVal }
}, { deep: true })
</script>

<style scoped lang="scss">
.collapsible-section {
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.collapsible-header {
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  background: rgba(0,161,96,0.06);
  border-radius: 4px;
  transition: all 0.3s ease;
}

.header-left {
  display: flex;
  align-items: center;
  .header-arrow {
    transform: rotate(-90deg);
    transform-origin: center center;
  }
}

.header-title {
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.field-count {
  display: none;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #F1F5F9;
  color: #64748B;
  font-weight: normal;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.collapse-text {
  font-size: 14px;
  color: #64748B;
}

.collapse-icon {
  font-size: 14px;
  color: #64748B;
  transition: transform 0.3s ease;
}

.collapse-icon.is-active {
  transform: rotate(180deg);
}

.collapsible-content {
  max-height: 0;
  padding-left: 24px;
  overflow: hidden;
  transition: all 0.3s ease-out;
  opacity: 0;
}

.collapsible-content.expanded {
  max-height: none;
  opacity: 1;
  padding-top: 16px;
}

.form-row {
  display: flex;
  gap: 16px;
  opacity: 0;
  transform: translateY(-10px);
  transition: all 0.3s ease;
  &:not(:last-child) {
    margin-bottom: 20px;
  }
}

.collapsible-content.expanded .form-row {
  opacity: 1;
  transform: translateY(0);
}

.form-item {
  flex: 1;
  display: flex;
  margin-bottom: 0;
  max-width: calc(50% - 12px);
  border: 1px solid #E1E1E1;
  border-radius: 4px;
}

.form-item label {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  text-align: center;
  color: var(--el-text-color-regular);
  border-right: 1px solid #E1E1E1;
}

.form-item input {
  width: 0;
  flex: 1;
  padding: 12px 14px;
  transition: all 0.3s ease;
  box-sizing: border-box;
  font-family: PingFang SC;
  border: none;
  border-radius: 4px;
}
.form-item input:disabled::placeholder {
  opacity: 0
}
.form-item input::placeholder {
  color: #999;
}

.form-item input:focus {
  outline: none;
  border-color: #1a1a1a;
  box-shadow: none;
}

/* 为不同表单项添加延迟动画 */
.collapsible-content .form-item:nth-child(1) { transition-delay: 0.1s; }
.collapsible-content .form-item:nth-child(2) { transition-delay: 0.15s; }
.collapsible-content .form-item:nth-child(3) { transition-delay: 0.2s; }
.collapsible-content .form-item:nth-child(4) { transition-delay: 0.25s; }
</style> 